import { IUser } from '@shared/user.interface';

declare global {
  interface Window {
    TalkdeskChatSDK?: unknown;
    webchat?: unknown;
  }
}

export type ChatContextType = 'submission' | 'study' | 'site' | 'user';

// Base interface for all chat types
interface BaseChatData {
  isOpen: boolean;
  contextType: ChatContextType;
  primaryId: string;
  user: IUser;
  question?: string;
}

export interface SubmissionChatData extends BaseChatData {
  contextType: 'submission';
  submissionId: string;
  submissionTitle?: string;
}

export interface StudyChatData extends BaseChatData {
  contextType: 'study';
  studyId: string;
  studyName?: string;
}

export interface SiteChatData extends BaseChatData {
  contextType: 'site';
  siteId: string;
  siteName?: string;
}

export interface UserChatData extends BaseChatData {
  contextType: 'user';
  userId: string;
  userName?: string;
}

// Union type of all possible chat data types
export type ChatData =
  | SubmissionChatData
  | StudyChatData
  | SiteChatData
  | UserChatData;

export interface TalkDeskState {
  chats: Record<string, ChatData>;
  activeChatId?: string;
}

// Helper to generate unique chat keys
export const generateChatKey = (
  contextType: ChatContextType,
  id: string,
): string => {
  return `${contextType}:${id}`;
};

export interface TalkdeskChatSDK {
  (elementId: string, config: TalkdeskConfig): TalkdeskWebchat;
}

export interface TalkdeskConfig {
  touchpointId: string;
  accountId: string;
  region: string;
}

export interface TalkdeskWebchat {
  init(options: TalkdeskInitOptions): Promise<void>;
  open(): void;
  setOnOpen(callback: (data: unknown) => void): void;
  setContextParam(params: Record<string, any>): void;
  set(event: string, callback: (data: unknown) => void): void;
  onConversationStart(callback: (data: unknown) => void): void;
  onOpenWebchat(callback: (data: unknown) => void): void;
}

export interface TalkdeskInitOptions {
  enableValidation?: boolean;
  enableEmoji?: boolean;
  enableUserInput?: boolean;
  enableAttachments?: boolean;
  showInitialScreen?: boolean;
  initialScreenFields?: TalkdeskInitialScreenField[];
  initialScreenTitle?: string;
  initialScreenSubmitButtonLabel?: string;
}

export interface TalkdeskInitialScreenField {
  id: string;
  label: string;
  type: string;
  required: boolean;
}
